import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import {
  api,
  ApiError,
  type PlayerStandingsPayload,
  type StandingsGroup,
  type StandingsRow,
} from "../lib/api";
import PlayerPhotoChip from "../components/PlayerPhotoChip";
import RankMovementBadge from "../components/RankMovementBadge";
import PlayerStageLockedCard from "../components/PlayerStageLockedCard";
import PlayerTournamentBottomNav from "../components/PlayerTournamentBottomNav";
import { playerStageLocked } from "../lib/playerStage";
import { repeatedStandingsGroupNames, standingsGroupLabel } from "../lib/groupLabels";
import TableScrollWrap from "../components/TableScrollWrap";
import Icon from "../components/icons";
import { scoreVocab, type ScoreVocab } from "../lib/labels";
import {
  initialRankDisplay,
  participantGroupScope,
  showPointsColumn,
  showPointsColumnInGroupView,
  divisionStartedChecker,
  tiebreakerSentence,
} from "../lib/standingsRules";

function StandingsTable({
  rows,
  highlightId,
  groupView = false,
  vocab,
  showPts = true,
  showRankingPoints = false,
  ariaLabel = "Tabla de posiciones",
  photos,
  started = () => true,
  cutAfter = null,
}: {
  rows: StandingsRow[];
  highlightId: number | null;
  groupView?: boolean;
  vocab: ScoreVocab;
  showPts?: boolean;
  /** Torneo rankeado por puntos (Eduardo 2026-09-24): columna Puntos. */
  showRankingPoints?: boolean;
  ariaLabel?: string;
  photos?: Record<string, string | null>;
  /** Eduardo 2026-10-01: sin resultados no hay rank actual ni movimientos.
   * Por categoría: ¿la categoría de esta fila ya tiene resultados? */
  started?: (divisionId: number | null | undefined) => boolean;
  /** Línea punteada bajo este lugar (pasan a playoffs). */
  cutAfter?: number | null;
}) {
  if (rows.length === 0) {
    return (
      <p className="muted">Sin jugadores en esta vista.</p>
    );
  }
  // Escalera (Eduardo 2026-09-07): en la vista de grupo se muestra el lugar
  // con el que cada jugador ENTRÓ al grupo y el lugar de la semana actual;
  // el movimiento es la diferencia entre ambos.
  const showGroupRanks = groupView && rows.some((row) => row.initial_group_rank != null);
  return (
    <TableScrollWrap ariaLabel={ariaLabel}>
      <table className={`data-table standings-table${groupView ? " standings-table--group-view" : ""}`}>
        <thead>
          <tr>
            <th>{groupView ? "Rank" : "#"}</th>
            <th>Jugador</th>
            {!groupView && <th>Grupo</th>}
            <th className="standings-col--num" title="Partidos jugados">PJ</th>
            <th className="standings-col--num" title="Partidos ganados">G</th>
            <th className="standings-col--num" title="Partidos perdidos">P</th>
            {/* Conflicto #131×#132 resuelto conservando ambos: visibilidad
                condicional por scope (showPts, #131) + title (#132). */}
            {showPts && <th className="standings-col--num" title="Puntos">Pts</th>}
            <th className="standings-col--score">{vocab.setsHeader}</th>
            <th className="standings-col--score">{vocab.ggHeader}–{vocab.gpHeader}</th>
            <th className="standings-col--dif">Dif</th>
            {!groupView && <th className="standings-col--num" title="Ranking inicial">Rank inicial</th>}
            {!groupView && <th className="standings-col--num" title="Ranking general acumulado">Rank actual</th>}
            {showGroupRanks && <th className="standings-col--num" title="Lugar con el que entró al grupo">Rank inicial</th>}
            {showGroupRanks && <th className="standings-col--num" title="Lugar en el grupo esta semana">Rank actual</th>}
            {showRankingPoints && <th className="standings-col--num" title="Puntos de ranking capturados por el organizador">Puntos</th>}
            <th className="standings-col--move">Movimiento</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isMe = row.participant_id === highlightId;
            return (
              <tr
                key={row.participant_id}
                className={[isMe ? "standings-row--me" : "", cutAfter != null && row.position === cutAfter ? "ps-cut-row" : ""].filter(Boolean).join(" ") || undefined}
              >
                <td>{row.position}</td>
                <td>
                  <span className="player-name-with-photo">
                    <PlayerPhotoChip
                      name={row.display_name}
                      photoUrl={photos?.[String(row.participant_id)] ?? null}
                      subtitle={row.group_name ?? null}
                    />
                    {row.player_profile_id ? (
                      <Link className="player-name-action" to={`/player/profiles/${row.player_profile_id}`}>
                        <strong>{row.display_name}</strong>
                      </Link>
                    ) : (
                      <strong>{row.display_name}</strong>
                    )}
                  </span>
                  {isMe && (
                    <span
                      className="status-pill status-pill--active"
                      style={{ marginLeft: "0.4rem" }}
                    >
                      Tú
                    </span>
                  )}
                </td>
                {!groupView && <td>{row.group_name ?? "—"}</td>}
                <td className="standings-col--num">{row.matches_played}</td>
                <td className="standings-col--num">{row.wins}</td>
                <td className="standings-col--num">{row.losses}</td>
                {showPts && (
                  <td className="standings-col--num">
                    <strong>{row.points}</strong>
                  </td>
                )}
                <td className="standings-col--score">
                  {row.sets_won}-{row.sets_lost}
                </td>
                <td className="standings-col--score">
                  {row.games_won}-{row.games_lost}
                </td>
                <td className="standings-col--dif">
                  {row.differential > 0
                    ? `+${row.differential}`
                    : row.differential}
                </td>
                {!groupView && (
                  <td className="standings-col--num">
                    {/* Los puntos ya tienen su columna «Puntos»: aquí el seed o S/R. */}
                    {initialRankDisplay(row.initial_rank ?? row.tournament_rank, null, false)}
                  </td>
                )}
                {!groupView && <td className="standings-col--num">{started(row.division_id) ? row.current_rank ?? "—" : "—"}</td>}
                {showGroupRanks && <td className="standings-col--num">{row.initial_group_rank ?? "—"}</td>}
                {showGroupRanks && <td className="standings-col--num">{row.group_position ?? row.position}</td>}
                {showRankingPoints && <td className="standings-col--num">{row.ranking_points ?? 0}</td>}
                <td className="standings-col--move">
                  {started(row.division_id) ? (
                    <RankMovementBadge
                      movement={row.rank_movement}
                      delta={row.rank_delta}
                    />
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableScrollWrap>
  );
}

/** Tabla corta del ranking (maqueta 2026-10-06): #, Jugador, PJ, G-P y DIF;
 * tu renglón en lima con «Tú». */
function CompactStandings({
  rows,
  highlightId,
  rankOf,
  ariaLabel,
  cutAfter = null,
}: {
  rows: StandingsRow[];
  highlightId: number | null;
  rankOf: (row: StandingsRow) => number | null;
  ariaLabel: string;
  cutAfter?: number | null;
}) {
  if (rows.length === 0) return <p className="muted">Sin jugadores en esta vista.</p>;
  return (
    <table className="ps-rank-table" aria-label={ariaLabel}>
      <thead>
        <tr>
          <th scope="col">#</th>
          <th scope="col">Jugador</th>
          <th scope="col" title="Partidos jugados">
            PJ
          </th>
          <th scope="col" title="Ganados-perdidos">
            G-P
          </th>
          <th scope="col" title="Diferencia">
            Dif
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isMe = row.participant_id === highlightId;
          const rank = rankOf(row);
          return (
            <tr
              key={row.participant_id}
              className={[isMe ? "is-me standings-row--me" : "", cutAfter != null && rank === cutAfter ? "ps-cut-row" : ""].filter(Boolean).join(" ") || undefined}
            >
              <td className="ps-rank-table__rank">{rank ?? "—"}</td>
              <td className="ps-rank-table__name">
                {row.player_profile_id ? (
                  <Link className="player-name-action" to={`/player/profiles/${row.player_profile_id}`}>
                    {row.display_name}
                  </Link>
                ) : (
                  row.display_name
                )}
                {isMe ? <span className="ps-you">Tú</span> : null}
              </td>
              <td>{row.matches_played}</td>
              <td>
                {row.wins}-{row.losses}
              </td>
              <td className="ps-rank-table__dif">{row.differential > 0 ? `+${row.differential}` : row.differential}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** «Pasan a playoffs» y «Desempate» del formato de la categoría (solo si existen). */
function GroupCutNotes({ group }: { group: StandingsGroup }) {
  const qualify = group.qualify_count ?? null;
  const wildcards = group.wildcard_count ?? null;
  const tiebreakers = group.tiebreakers ?? [];
  if (!qualify && !wildcards && tiebreakers.length === 0) return null;
  return (
    <div className="ps-cut-notes">
      {qualify ? (
        <p className="ps-cut-notes__qualify">
          Pasan a playoffs: {qualify}
          {wildcards ? (
            <span className="muted">
              {" "}
              · + {wildcards} {wildcards === 1 ? "mejor" : "mejores"}{" "}
              {group.wildcard_position ? `${group.wildcard_position}.º ${wildcards === 1 ? "lugar" : "lugares"}` : "comodines"}
            </span>
          ) : null}
        </p>
      ) : null}
      {tiebreakers.length > 0 ? (
        <p className="muted ps-cut-notes__tiebreak">Desempate: {tiebreakerSentence(tiebreakers)}</p>
      ) : null}
    </div>
  );
}

interface PlayerStandingsPageProps {
  groupsOnly?: boolean;
}

export default function PlayerStandingsPage({
  groupsOnly = false,
}: PlayerStandingsPageProps = {}) {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token } = useAuth();

  const [data, setData] = useState<PlayerStandingsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterGroupId, setFilterGroupId] = useState<number | "general">(
    "general",
  );
  // Cuenta familiar (Eduardo 2026-09-29): «Tú», tu grupo y tu partido son
  // los del perfil elegido para este torneo.
  const scope = usePlayerProfileScope({ tournamentId });
  const scopeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);
  // Ranking por grupos (maqueta 2026-10-06): «Mi grupo» / «General», grupos
  // abiertos a mano y tablas con sets y movimiento.
  const [rankingView, setRankingView] = useState<"group" | "general">("group");
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [detailedKeys, setDetailedKeys] = useState<Record<string, boolean>>({});
  const [generalDetailed, setGeneralDetailed] = useState(false);

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId) || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const response = await api.standings.forPlayer(token, tournamentId, scopeProfileId);
      if (!latestLoad.isCurrent(attempt)) return;
      setData(response.data);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar los standings.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [latestLoad, scope.ready, scopeProfileId, token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Eduardo 2026-09-24: un grupo inactivo no se muestra al jugador; y el
  // torneo "rankeado por puntos" enseña la columna Puntos.
  const activeGroups = useMemo(
    () => (data?.groups ?? []).filter((g) => g.is_active !== false),
    [data],
  );
  // «Grupo 1» de Sub-10 y de Sub-12 (Eduardo 2026-10-01): los nombres
  // repetidos llevan su categoría.
  const repeatedGroupNames = useMemo(
    () => repeatedStandingsGroupNames(data?.groups ?? []),
    [data],
  );
  const showRankingPoints = useMemo(
    () => (data?.ranking ?? []).some((row) => (row.ranking_points ?? 0) > 0),
    [data],
  );
  const stageLocked = playerStageLocked(data?.player_visibility);
  const started = useMemo(() => divisionStartedChecker(data), [data]);

  const filterTabs = useMemo(() => {
    const tabs: Array<{ value: number | "general"; label: string }> = [
      { value: "general", label: "General" },
    ];
    activeGroups.forEach((g) => {
      tabs.push({
        value: g.group_id ?? -1,
        label: standingsGroupLabel(g, repeatedGroupNames),
      });
    });
    return tabs;
  }, [activeGroups, repeatedGroupNames]);

  const visibleRows: StandingsRow[] = useMemo(() => {
    if (!data) return [];
    if (filterGroupId === "general") return data.ranking;
    const group = data.groups.find(
      (g) => (g.group_id ?? -1) === filterGroupId,
    );
    return group ? group.standings : [];
  }, [data, filterGroupId]);

  // PTS por SCOPE: con un grupo filtrado mandan las reglas de ESE grupo (en
  // reglas mixtas la bandera root es ambigua); su ausencia = histórico.
  const visibleShowPts = useMemo(() => {
    if (!data) return true;
    if (filterGroupId === "general") return showPointsColumn(data);
    const group = data.groups.find(
      (g) => (g.group_id ?? -1) === filterGroupId,
    );
    return showPointsColumn(group ?? data);
  }, [data, filterGroupId]);

  // El landmark de la tabla visible sigue al filtro activo: con una pestaña
  // de grupo seleccionada, anunciar "general" sería información falsa para
  // un lector de pantalla (gate #132).
  const visibleAriaLabel = useMemo(() => {
    if (filterGroupId === "general") return "Tabla de posiciones general";
    const group = data?.groups.find(
      (g) => (g.group_id ?? -1) === filterGroupId,
    );
    return `Tabla de posiciones: ${group ? standingsGroupLabel(group, repeatedGroupNames) : "grupo seleccionado"}`;
  }, [data, filterGroupId, repeatedGroupNames]);

  // "Mi posición" usa las reglas de LA CATEGORÍA DEL PARTICIPANTE (su grupo
  // en el payload), no la bandera root.
  const myGroupScope = useMemo(
    () => participantGroupScope(data?.groups, data?.my_participant_id),
    [data],
  );

  const vocab = scoreVocab(data?.sport);

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  if (groupsOnly) {
    const myId = data?.my_participant_id ?? null;
    const myRow = data?.my_row ?? null;
    const myGroup = myGroupScope && myGroupScope.group_id != null ? myGroupScope : null;
    const myGroupRank = myGroup?.standings.find((row) => row.participant_id === myId)?.position ?? null;
    const myDivisionId = myRow?.division_id ?? null;
    // «General» = el ranking VIVO de TU categoría: el orden que ya calcula el
    // backend (victorias/puntos + desempates de la categoría), numerado 1..N.
    // Antes se ordenaba por current_rank (persistido, de la siembra): en el
    // torneo 67 salían jugadores 2-0 debajo de 1-1 y un «46» al final.
    const generalRows = myRow
      ? (data?.ranking ?? []).filter((row) => (row.division_id ?? null) === myDivisionId)
      : [];
    const generalRankById = new Map(generalRows.map((row, idx) => [row.participant_id, idx + 1]));
    const generalRank = (row: StandingsRow) => generalRankById.get(row.participant_id) ?? row.position;
    const showGeneral = !stageLocked && generalRows.length > 0;
    const view = showGeneral ? rankingView : "group";
    const diff = myRow?.differential ?? null;
    const diffText = diff === null ? "—" : diff > 0 ? `+${diff}` : String(diff);
    const divisionLabel = myRow?.division_name?.trim() || null;

    const renderGroup = (group: (typeof activeGroups)[number]) => {
      const key = String(group.group_id ?? "none");
      const isPlayerGroup = group.standings.some((row) => row.participant_id === myId);
      const open = openGroups[key] ?? isPlayerGroup;
      const detailed = detailedKeys[key] ?? false;
      const label = standingsGroupLabel(group, repeatedGroupNames);
      const bodyId = `ps-ranking-group-${key}`;
      return (
        <section
          key={key}
          className={`ps-group player-ranking-group standings-group${isPlayerGroup ? " is-own is-player-group" : ""}${open ? " is-open" : ""}`}
        >
          <button
            type="button"
            className="ps-group__toggle"
            aria-expanded={open}
            aria-controls={open ? bodyId : undefined}
            onClick={() => setOpenGroups((prev) => ({ ...prev, [key]: !open }))}
          >
            <span className="ps-group__title">
              {label}
              {isPlayerGroup ? <span className="ps-tag">Tu grupo</span> : null}
            </span>
            <span className="ps-group__count">
              {group.standings.length} jugadores
              <Icon name="chevron-down" className="ps-group__chevron" />
            </span>
          </button>
          {open ? (
            <div id={bodyId} className="ps-group__body">
              {detailed ? (
                <StandingsTable
                  rows={group.standings}
                  highlightId={myId}
                  groupView
                  vocab={vocab}
                  showPts={showPointsColumnInGroupView(group)}
                  showRankingPoints={showRankingPoints}
                  ariaLabel={`Tabla de posiciones: ${label}`}
                  photos={data?.photos}
                  started={started}
                  cutAfter={group.qualify_count ?? null}
                />
              ) : (
                <CompactStandings
                  rows={group.standings}
                  highlightId={myId}
                  rankOf={(row) => row.position}
                  ariaLabel={`Tabla de posiciones: ${label}`}
                  cutAfter={group.qualify_count ?? null}
                />
              )}
              <GroupCutNotes group={group} />
              <button
                type="button"
                className="ps-outline-button"
                aria-expanded={detailed}
                onClick={() => setDetailedKeys((prev) => ({ ...prev, [key]: !detailed }))}
              >
                {detailed ? "Ver tabla corta" : "Ver sets y movimiento"}
              </button>
            </div>
          ) : null}
        </section>
      );
    };

    return (
      <div className="pt-reto-theme ps-page">
        {/* Secciones del torneo (Eduardo 2026-10-05): en computadora, pestañas
            aquí arriba; en celular, la barra flotante de abajo. */}
        <PlayerTournamentBottomNav tournamentId={tournamentId} />
        <PlayerProfileScopeSwitcher scope={scope} tournament />
        <header className="ps-head">
          {divisionLabel ? <span className="ps-head__eyebrow">{divisionLabel}</span> : null}
          <h1>Ranking</h1>
        </header>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}

        {loading ? (
          <section className="ps-card">
            <p className="muted">Cargando…</p>
          </section>
        ) : !data ? (
          <section className="ps-card">
            <p className="muted">Sin datos.</p>
          </section>
        ) : stageLocked ? (
          <PlayerStageLockedCard tournamentId={tournamentId} section="ranking" />
        ) : (
          <>
            {myRow ? (
              <section className="pt-home__reto ps-reto ps-position" aria-label="Tu posición">
                <span className="pt-home__eyebrow">Tu posición</span>
                <div className="ps-position__ranks">
                  <div className="ps-position__rank">
                    <span className="ps-position__label">En tu grupo</span>
                    <span className="ps-position__value">{myGroupRank != null ? `#${myGroupRank}` : "—"}</span>
                    <span className="ps-position__sub">
                      {myGroup
                        ? `de ${myGroup.standings.length} · ${standingsGroupLabel(myGroup, repeatedGroupNames)}`
                        : "Sin grupo"}
                    </span>
                  </div>
                  <div className="ps-position__rank">
                    <span className="ps-position__label">General</span>
                    <span className="ps-position__value">{data.my_position != null ? `#${data.my_position}` : "—"}</span>
                    <span className="ps-position__sub">
                      {generalRows.length > 0 ? `de ${generalRows.length}` : ""}
                      {generalRows.length > 0 && divisionLabel ? " · " : ""}
                      {divisionLabel ?? ""}
                    </span>
                  </div>
                </div>
                <dl className="ps-position__stats">
                  <div>
                    <dd>{`${myRow.wins}–${myRow.losses}`}</dd>
                    <dt>Récord</dt>
                  </div>
                  <div>
                    <dd>{`${myRow.games_won}–${myRow.games_lost}`}</dd>
                    <dt>
                      {vocab.ggHeader}–{vocab.gpHeader}
                    </dt>
                  </div>
                  <div>
                    <dd className={diff !== null && diff >= 0 ? "is-accent" : undefined}>{diffText}</dd>
                    <dt>Dif</dt>
                  </div>
                </dl>
              </section>
            ) : null}

            {showGeneral ? (
              <div className="ps-segmented" role="tablist" aria-label="Qué ranking ver">
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === "group"}
                  className={view === "group" ? "is-active" : undefined}
                  onClick={() => setRankingView("group")}
                >
                  Mi grupo
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === "general"}
                  className={view === "general" ? "is-active" : undefined}
                  onClick={() => setRankingView("general")}
                >
                  General
                </button>
              </div>
            ) : null}

            {view === "general" ? (
              <section className="ps-card" aria-labelledby="ps-ranking-general">
                <div className="ps-card__row">
                  <h2 id="ps-ranking-general" className="ps-card__title">
                    {divisionLabel ? `General · ${divisionLabel}` : "General"}
                  </h2>
                  <span className="ps-muted">{generalRows.length} jugadores</span>
                </div>
                {generalDetailed ? (
                  <StandingsTable
                    rows={generalRows}
                    highlightId={myId}
                    vocab={vocab}
                    showPts={showPointsColumn(myGroupScope ?? data)}
                    showRankingPoints={showRankingPoints}
                    ariaLabel="Tabla de posiciones general"
                    photos={data.photos}
                    started={started}
                  />
                ) : (
                  <CompactStandings
                    rows={generalRows}
                    highlightId={myId}
                    rankOf={generalRank}
                    ariaLabel="Tabla de posiciones general"
                  />
                )}
                <button
                  type="button"
                  className="ps-outline-button"
                  aria-expanded={generalDetailed}
                  onClick={() => setGeneralDetailed((value) => !value)}
                >
                  {generalDetailed ? "Ver tabla corta" : "Ver sets y movimiento"}
                </button>
              </section>
            ) : (
              <div className="ps-groups standings-groups-list">
                {[...activeGroups]
                  .sort((a, b) => {
                    const aMine = a.standings.some((row) => row.participant_id === myId) ? 0 : 1;
                    const bMine = b.standings.some((row) => row.participant_id === myId) ? 0 : 1;
                    return aMine - bMine;
                  })
                  .map(renderGroup)}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Secciones del torneo (Eduardo 2026-10-05): en computadora, pestañas
          aquí arriba; en celular, la barra flotante de abajo. */}
      <PlayerTournamentBottomNav tournamentId={tournamentId} />
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">
              Ranking
            </h1>
            <p className="card__subtitle">
              {groupsOnly
                ? "Consulta el ranking por grupo del torneo. Tu grupo queda abierto y resaltado."
                : "Tabla de posiciones del torneo en el que estás inscrito."}
            </p>
          </div>
          <div className="form__actions">
            <Link
              className="btn btn--ghost"
              to={`/player/tournaments/${tournamentId}/schedule`}
            >
              Calendario
            </Link>
            <Link
              className="btn btn--ghost"
              to={`/player/tournaments/${tournamentId}/rules`}
            >
              Reglamento
            </Link>
            <Link className="btn btn--ghost" to="/player">
              Volver
            </Link>
          </div>
        </div>
        <PlayerProfileScopeSwitcher scope={scope} tournament />
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
      </section>

      {data && data.my_row && (
        <section className="card">
          <h2 className="card__title">Mi posición</h2>
          <p className="card__subtitle">
            Posición #{data.my_position} ·{" "}
            {showPointsColumn(myGroupScope ?? data) ? `${data.my_row.points} pts · ` : ""}
            {data.my_row.wins}G / {data.my_row.losses}P · Dif{" "}
            {data.my_row.differential > 0
              ? `+${data.my_row.differential}`
              : data.my_row.differential}
          </p>
        </section>
      )}

      <section className="card">
        <div className="card__header-row">
          <h2 className="card__title">
            {groupsOnly ? "Ranking por grupos" : "Tabla de posiciones"}
          </h2>
          {!groupsOnly && (
            <div className="standings-tabs">
              {filterTabs.map((tab) => (
                <button
                  key={String(tab.value)}
                  type="button"
                  className={`btn btn--ghost btn--compact${
                    filterGroupId === tab.value ? " standings-tab--active" : ""
                  }`}
                  onClick={() => setFilterGroupId(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {loading ? (
          <p className="muted">Cargando…</p>
        ) : !data ? (
          <p className="muted">Sin datos.</p>
        ) : stageLocked ? (
          <PlayerStageLockedCard tournamentId={tournamentId} section="ranking" />
        ) : groupsOnly ? (
          <div className="standings-groups-list">
            {activeGroups.map((group) => {
              const isPlayerGroup = group.standings.some(
                (row) => row.participant_id === data.my_participant_id,
              );
              return (
                <details
                  key={String(group.group_id ?? "none")}
                  className={`player-ranking-group standings-group${isPlayerGroup ? " is-player-group" : ""}`}
                  open={isPlayerGroup}
                >
                  <summary className="player-ranking-group__summary standings-group__summary">
                    <strong>{standingsGroupLabel(group, repeatedGroupNames)}</strong>
                    {isPlayerGroup && <span className="status-pill status-pill--active">Tu grupo</span>}
                    <span className="muted">{group.standings.length} jugadores</span>
                    <span aria-hidden="true">▾</span>
                  </summary>
                  <StandingsTable
                    rows={group.standings}
                    highlightId={data.my_participant_id}
                    groupView
                    vocab={vocab}
                    showPts={showPointsColumnInGroupView(group)}
                    showRankingPoints={showRankingPoints}
                    ariaLabel={`Tabla de posiciones: ${standingsGroupLabel(group, repeatedGroupNames)}`}
                    photos={data.photos}
                    started={started}
                  />
                </details>
              );
            })}
          </div>
        ) : (
          <StandingsTable
            rows={visibleRows}
            highlightId={data.my_participant_id}
            vocab={vocab}
            showPts={visibleShowPts}
            showRankingPoints={showRankingPoints}
            ariaLabel={visibleAriaLabel}
            photos={data.photos}
            started={started}
          />
        )}
      </section>
    </>
  );
}
